import React, { useState } from 'react';
import {
  MobileHeader,
  MobileViewSwitcher,
  MobileCommunityCards,
  MobileHoleCards,
  ActionButton,
  PokerActionButtons,
  EmptyPlayerSlot,
  renderCard
} from '../GameUIHelpers';
import BettingWheel from './BettingWheel';
import {
  MobileLeaderboard,
  GameStatistics
} from '../GameStatsHelpers';

// =============================================================================
// MOBILE LAYOUT COMPONENTS
// =============================================================================

/**
 * Mobile Lobby Layout Component
 */
export const MobileLobby = ({
  players,
  myPlayerId,
  gameInProgress,
  currentPlayer,
  gameState,
  p2pGameMode,
  showLeaderboard,
  onStartGame,
  onStartP2PGame,
  onViewTable,
  onRebuy,
  onLeaveRoom,
  onToggleLeaderboard,
  isLoading
}: {
  players: any[];
  myPlayerId: string;
  gameInProgress: boolean;
  currentPlayer: any;
  gameState: any;
  p2pGameMode: boolean;
  showLeaderboard: boolean;
  onStartGame: () => void;
  onStartP2PGame: () => void;
  onViewTable: () => void;
  onRebuy: () => void;
  onLeaveRoom: () => void;
  onToggleLeaderboard: () => void;
  isLoading: boolean;
}) => {
  const playersWithChips = players.filter(p => (p.chips || 0) > 0 && (p.isConnected === true || p.isConnected === undefined));

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">🃏 Poker Lobby</h1>
          <p className="text-gray-400">
            {p2pGameMode ? 'P2P Mode - Secure Gaming' : 'Server Mode - Fast Gaming'}
          </p>
        </div>

        {/* Players List */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">
              Players ({players.length}/6)
            </h2>
            {players.length > 0 && (
              <button
                onClick={onToggleLeaderboard}
                className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium transition-colors"
              >
                🏆
              </button>
            )}
          </div>

          <div className="space-y-3">
            {players.map((player) => (
              <div 
                key={player.id} 
                className={`p-3 rounded-lg border ${
                  player.id === myPlayerId 
                    ? 'bg-blue-900/50 border-blue-600' 
                    : 'bg-gray-700 border-gray-600'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 ${
                      player.isConnected !== false ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {player.name}
                        {player.id === myPlayerId && (
                          <span className="ml-2 text-blue-400 text-sm">(You)</span>
                        )}
                      </p>
                      <p className="text-gray-400 text-sm">
                        ${player.chips || 0} chips
                      </p>
                    </div>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    player.isConnected !== false ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                </div>
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: Math.max(0, 6 - players.length) }, (_, i) => (
              <div key={`empty-${i}`} className="p-3 rounded-lg bg-gray-700/50 border-2 border-dashed border-gray-600">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-gray-400 text-sm">?</span>
                  </div>
                  <p className="text-gray-500">Waiting for player...</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        {showLeaderboard && (
          <MobileLeaderboard 
            players={players}
            myPlayerId={myPlayerId}
            onClose={onToggleLeaderboard}
          />
        )}

        {/* Game Controls */}
        <div className="space-y-4">
          {playersWithChips.length >= 2 && !gameInProgress ? (
            <div className="relative">
              <ActionButton
                onClick={() => {
                  console.log('🔘 Mobile start button clicked!', { p2pGameMode, isLoading });
                  if (p2pGameMode) {
                    onStartP2PGame();
                  } else {
                    onStartGame();
                  }
                }}
                disabled={isLoading}
                variant="success"
                size="large"
                className="w-full shadow-lg touch-manipulation"
              >
                {isLoading ? '🔄 Starting Game...' : '🎮 Start Game'}
              </ActionButton>
              
              <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                p2pGameMode 
                  ? 'bg-purple-600 text-purple-100 border border-purple-400' 
                  : 'bg-gray-600 text-gray-100 border border-gray-400'
              }`}>
                {p2pGameMode ? 'P2P' : 'Server'}
              </div>
            </div>
          ) : (
            <ActionButton
              onClick={() => {}}
              disabled={true}
              variant="secondary"
              size="large"
              className="w-full shadow-lg"
            >
              Need {2 - playersWithChips.length} more player(s) with chips
            </ActionButton>
          )}

          {gameInProgress && (
            <ActionButton
              onClick={onViewTable}
              variant="primary"
              size="large"
              className="w-full shadow-lg touch-manipulation"
            >
              🎯 View Table →
            </ActionButton>
          )}

          {/* Rebuy Button */}
          {(currentPlayer?.chips || 0) === 0 && !gameInProgress && (currentPlayer?.gameRebuys || 0) < 5 && (
            <div>
              <ActionButton
                onClick={onRebuy}
                variant="success"
                size="large"
                className="w-full shadow-lg touch-manipulation"
              >
                💵 Rebuy (+$1000)
              </ActionButton>
              <p className="text-yellow-400 text-xs mt-1 text-center font-medium">
                🔄 {(currentPlayer?.gameRebuys || 0)}/5 rebuys ({5 - (currentPlayer?.gameRebuys || 0)} left)
              </p>
              <p className="text-gray-400 text-xs mt-1 text-center">
                Out of chips - rebuy to play!
              </p>
            </div>
          )}

          {/* Max rebuys message */}
          {(currentPlayer?.chips || 0) === 0 && !gameInProgress && (currentPlayer?.gameRebuys || 0) >= 5 && (
            <div className="p-3 bg-red-900/30 border border-red-500 rounded text-center">
              <p className="text-red-400 font-medium">🚫 Max rebuys reached (5/5)</p>
              <p className="text-gray-400 text-xs mt-1">Next game: 0/5 rebuys</p>
            </div>
          )}

          <ActionButton
            onClick={onLeaveRoom}
            variant="danger"
            size="normal"
            className="w-full shadow-lg touch-manipulation"
          >
            🚪 Leave Room
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Mobile Table Layout Component
 */
export const MobileTable = ({
  gameState,
  holeCards,
  players,
  myPlayerId,
  currentPlayer,
  mobileTableView,
  showLeaderboard,
  canCheck,
  callAmount,
  p2pGameMode,
  timeoutWarnings,
  showdownResults,
  onBackToLobby,
  onViewChange,
  onToggleLeaderboard,
  onPokerAction,
  onRebuy,
  onDismissShowdown
}: {
  gameState: any;
  holeCards: string[];
  players: any[];
  myPlayerId: string;
  currentPlayer: any;
  mobileTableView: string;
  showLeaderboard: boolean;
  canCheck: boolean;
  callAmount: number;
  p2pGameMode: boolean;
  timeoutWarnings: Set<string>;
  showdownResults: any;
  onBackToLobby: () => void;
  onViewChange: (view: string) => void;
  onToggleLeaderboard: () => void;
  onPokerAction: (action: string, amount?: number) => void;
  onRebuy: () => void;
  onDismissShowdown: () => void;
}) => {
  const [selectedBetAmount, setSelectedBetAmount] = useState(40);
  const [showBettingWheel, setShowBettingWheel] = useState(false);
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <MobileHeader 
        onBackClick={onBackToLobby}
        title="Table"
        pot={gameState?.pot}
      />
      
      {/* View Switcher */}
      <MobileViewSwitcher 
        currentView={mobileTableView}
        onViewChange={onViewChange}
        onLeaderboardClick={onToggleLeaderboard}
      />

      {/* Game Content */}
      <div className="flex-1 p-4 flex flex-col pb-6">
        {mobileTableView === 'table' ? (
          <>
            {/* Community Cards */}
            <MobileCommunityCards gameState={gameState} />

            {/* Leaderboard */}
            {showLeaderboard && (
              <MobileLeaderboard 
                players={players}
                myPlayerId={myPlayerId}
                onClose={onToggleLeaderboard}
              />
            )}
            
            {/* Showdown Results */}
            {showdownResults && (
              <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-4 mb-4">
                <h2 className="text-white text-xl font-bold text-center mb-3">
                  🎉 {showdownResults.type === 'showdown' ? 'RESULTS' : 'GAME OVER'} 🎉
                </h2>
                
                {showdownResults.type === 'showdown' ? (
                  <div className="text-center">
                    <div className="text-white text-lg font-bold">
                      Winner: {showdownResults.winners[0]?.name || 'Unknown'}
                    </div>
                    <div className="text-yellow-200 text-sm">{showdownResults.winningHand?.name}</div>
                    <div className="text-green-200 font-bold">${showdownResults.pot}</div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-white text-lg font-bold">Winner: {showdownResults.winners[0]?.name}</div>
                    <div className="text-yellow-200 text-sm">{showdownResults.winningReason}</div>
                  </div>
                )}
                
                <div className="text-center mt-3">
                  <button 
                    onClick={onDismissShowdown}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-1 rounded text-sm font-semibold transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Player Cards */}
            <MobileHoleCards holeCards={holeCards} />
          </>
        ) : (
          // Statistics View
          <GameStatistics 
            players={players}
            gameState={gameState}
            myPlayerId={myPlayerId}
          />
        )}

        {/* Action Buttons */}
        <div className="mt-auto pb-6">
          {gameState && gameState.currentPlayerTurn === myPlayerId ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <ActionButton
                  onClick={() => onPokerAction('fold')}
                  variant="danger"
                  size="large"
                  className="shadow-lg py-4 px-2 touch-manipulation"
                >
                  🔓 FOLD
                </ActionButton>
                <ActionButton
                  onClick={() => onPokerAction(canCheck ? 'check' : 'call', callAmount)}
                  variant="primary"
                  size="large"
                  className="shadow-lg py-4 px-2 touch-manipulation"
                >
                  {canCheck ? '✓ CHECK' : 
                    <div className="flex flex-col items-center">
                      <div className="text-sm">📞 CALL</div>
                      <div>${callAmount}</div>
                    </div>
                  }
                </ActionButton>
              </div>
              
              {/* Raise and All-In buttons */}
              <div className="grid grid-cols-2 gap-3">
                <ActionButton
                  onClick={() => onPokerAction('raise', selectedBetAmount)}
                  variant="success"
                  className="shadow-lg py-4 px-2 touch-manipulation text-sm"
                >
                  <div className="flex flex-col items-center">
                    <div className="text-sm">🎯 RAISE</div>
                    <div>${selectedBetAmount}</div>
                  </div>
                </ActionButton>
                <ActionButton
                  onClick={() => onPokerAction('all-in')}
                  disabled={currentPlayer?.chips === 0}
                  variant="warning"
                  className="shadow-lg py-4 px-2 touch-manipulation text-sm"
                >
                  <div className="flex flex-col items-center">
                    <div className="text-sm">💥 ALL-IN</div>
                    <div>${currentPlayer?.chips || 0}</div>
                  </div>
                </ActionButton>
              </div>
              
              {/* Betting Wheel */}
              <div className="mt-4 mb-4 flex justify-center">
                <BettingWheel
                  onAmountChange={(amount) => setSelectedBetAmount(amount)}
                  minBet={Math.max(gameState?.currentBet + (gameState?.minRaise || 20), 20)}
                  maxBet={currentPlayer?.chips || 1000}
                  currentBet={gameState?.currentBet || 0}
                  increment={10}
                />
              </div>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg text-center shadow-lg py-6 px-4 min-h-[60px] flex items-center justify-center">
              <div>
                <p className="text-gray-400 text-lg">⏳ Waiting for</p>
                <p className="text-yellow-400 font-semibold">
                  {players.find(p => p.id === gameState?.currentPlayerTurn)?.name || 'player'}
                </p>
              </div>
            </div>
          )}

          {/* Rebuy Button for Mobile Table */}
          {(currentPlayer?.chips || 0) === 0 && !gameState && (currentPlayer?.gameRebuys || 0) < 5 && (
            <div className="mt-4">
              <ActionButton
                onClick={onRebuy}
                variant="success"
                size="large"
                className="w-full shadow-lg touch-manipulation"
              >
                💵 REBUY (+$1000)
              </ActionButton>
              <p className="text-yellow-400 text-xs mt-1 text-center font-medium">
                🔄 {(currentPlayer?.gameRebuys || 0)}/5 rebuys ({5 - (currentPlayer?.gameRebuys || 0)} left)
              </p>
            </div>
          )}

          {/* Max rebuys message */}
          {(currentPlayer?.chips || 0) === 0 && !gameState && (currentPlayer?.gameRebuys || 0) >= 5 && (
            <div className="mt-4 p-3 bg-red-900/30 border border-red-500 rounded text-center">
              <p className="text-red-400 font-medium">🚫 Max rebuys reached (5/5)</p>
              <p className="text-gray-400 text-xs mt-1">You can rebuy again in the next game</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DESKTOP LAYOUT COMPONENTS
// =============================================================================

/**
 * Desktop Lobby Layout Component
 */
export const DesktopLobby = ({
  children
}: {
  children: React.ReactNode;
}) => (
  <div className="min-h-screen bg-gray-900">
    {/* Header */}
    <header className="bg-gray-800 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">
              🃏 P2P Poker
            </h1>
            <div className="text-sm text-gray-400">
              Decentralized Texas Hold'em
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/about" className="text-gray-300 hover:text-white transition-colors">
              About
            </a>
            <div className="text-sm text-gray-400">
              {import.meta.env.VITE_VERSION || 'v1.4.0'} {import.meta.env.VITE_ENV === 'development' && '(DEV)'}
            </div>
          </div>
        </div>
      </div>
    </header>

    {/* Content */}
    <div className="max-w-2xl mx-auto px-4 py-12">
      {children}
    </div>
  </div>
);